import React, { useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgress, Typography } from '@material-ui/core';
import { useDispatch } from 'react-redux';

import MyTextField from '../utils/MyTextField';
import UnoButton from '../utils/UnoButton';
import allActions from '../../actions';
import { UserAPI } from '../../API/user';
import { isValidCpf, justNumbers, applyMaskPhone } from '../utils/utils';
import { unoTheme } from '../../assets/styles/unoTheme';

const useStyles = makeStyles((theme) => ({
    dialogTitle: {
        '& h2': {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
        }
    },
    actionsFlex: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    loaderWrapper: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(3),
    },
    userBlock: {
        marginTop: theme.spacing(2),
        textAlign: 'left',
    },
    infoRow: {
        marginBottom: theme.spacing(0.5),
    },
    sectionTitle: {
        fontWeight: 'bold',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(0.5),
    },
    chip: {
        display: 'inline-block',
        padding: '2px 8px',
        marginRight: '6px',
        marginBottom: '6px',
        borderRadius: '10px',
        backgroundColor: unoTheme.secondaryTuplaTable,
        fontSize: '12px',
    },
    notFound: {
        marginTop: theme.spacing(2),
        textAlign: 'center',
        color: unoTheme.redMoney,
        fontWeight: 'bold',
    },
    warning: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(1),
        borderRadius: '8px',
        backgroundColor: '#fff3cd',
        color: unoTheme.warningColor || '#856404',
        textAlign: 'center',
        fontWeight: 'bold',
    },
}));

// Labels amigáveis para as roles esperadas em cada aba
const ROLE_LABELS = {
    ROLE_ADMIN: 'Administrador',
    ROLE_ADVISOR: 'Consultor',
    ROLE_MANAGER: 'Gestor',
    ROLE_SUBMANAGER: 'Subgestor',
    ROLE_VIEWER: 'Visualizador',
    ROLE_SUPER_ADMIN: 'Super Admin',
};

export default function LinkUserModal({ open, clientOnScreen, roleTab, handleClose }) {

    const classes = useStyles();
    const dispatch = useDispatch();

    const [inputCpf, setInputCpf] = useState('');
    const [searching, setSearching] = useState(false);
    const [linking, setLinking] = useState(false);
    const [searchedCpf, setSearchedCpf] = useState(null);
    const [foundUser, setFoundUser] = useState(undefined); // undefined = sem busca; null = não encontrado

    const roleTabLabel = roleTab ? (ROLE_LABELS[roleTab.title] || roleTab.title) : '';

    async function doSearch(cpf) {
        setSearching(true);
        setFoundUser(undefined);

        const response = await UserAPI.getUserByCpf(cpf);

        setSearching(false);

        if (response.success && response.body && response.body.found) {
            setFoundUser(response.body.user);
        } else {
            setFoundUser(null);
        }
    }

    function onChangeCpf(value) {
        setInputCpf(value);

        const cleanCpf = justNumbers(value);

        if (isValidCpf(cleanCpf)) {
            // Evita repetir a mesma busca
            if (cleanCpf !== searchedCpf) {
                setSearchedCpf(cleanCpf);
                doSearch(value);
            }
        } else {
            setSearchedCpf(null);
            setFoundUser(undefined);
        }
    }

    const hasRequiredRole = foundUser && roleTab
        && Array.isArray(foundUser.roles)
        && foundUser.roles.some(r => r.id === roleTab.id);

    const alreadyLinked = foundUser && clientOnScreen
        && Array.isArray(foundUser.clients)
        && foundUser.clients.some(c => c.id === clientOnScreen.id);

    const canLink = foundUser && hasRequiredRole && !alreadyLinked && !linking;

    async function doLink() {
        if (!canLink) return;

        setLinking(true);

        const response = await UserAPI.linkExistingUserToClient(
            foundUser.id,
            clientOnScreen.id,
            roleTab.id
        );

        setLinking(false);

        if (response.success) {
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                type: 'success',
                message: 'Usuário vinculado com sucesso',
            }));
            handleClose(true);
        } else {
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                type: 'error',
                message: (response.body && response.body.message) || 'Não foi possível vincular o usuário',
            }));
        }
    }

    return (
        <Dialog
            fullWidth={true}
            maxWidth={'sm'}
            open={open}
            onClose={() => handleClose(false)}>

            <DialogTitle className={classes.dialogTitle}>
                {`Vincular ${roleTabLabel || 'usuário'}`}
            </DialogTitle>

            <DialogContent>

                <MyTextField
                    fullWidth={true}
                    value={inputCpf}
                    name="cpf-link-user"
                    size={'small'}
                    onChange={onChangeCpf}
                    variant="outlined"
                    helperText={"Insira um CPF válido"}
                    autoFocus={true}
                    mask={['cpf']}
                    label="CPF" />

                {searching && (
                    <div className={classes.loaderWrapper}>
                        <CircularProgress size={28} />
                    </div>
                )}

                {!searching && foundUser === null && (
                    <Typography className={classes.notFound}>
                        Usuário não encontrado
                    </Typography>
                )}

                {!searching && foundUser && (
                    <div className={classes.userBlock}>
                        <Typography className={classes.infoRow}><b>Nome:</b> {foundUser.name}</Typography>
                        <Typography className={classes.infoRow}><b>Email:</b> {foundUser.email}</Typography>
                        <Typography className={classes.infoRow}><b>Contato:</b> {applyMaskPhone(foundUser.phone_number) || '-'}</Typography>
                        <Typography className={classes.infoRow}><b>Situação:</b> {foundUser.active ? 'Ativo' : 'Inativo'}</Typography>

                        <Typography className={classes.sectionTitle}>Permissões (roles)</Typography>
                        {foundUser.roles && foundUser.roles.length > 0
                            ? foundUser.roles.map(r => (
                                <span key={'role-' + r.id} className={classes.chip}>
                                    {ROLE_LABELS[r.role_type] || r.role_type}
                                </span>
                            ))
                            : <Typography>Nenhuma permissão</Typography>}

                        <Typography className={classes.sectionTitle}>Clientes vinculados</Typography>
                        {foundUser.clients && foundUser.clients.length > 0
                            ? foundUser.clients.map(c => (
                                <span key={'client-' + c.id} className={classes.chip}>
                                    {c.label_name}
                                </span>
                            ))
                            : <Typography>Nenhum cliente vinculado</Typography>}

                        {!hasRequiredRole && (
                            <Typography className={classes.warning}>
                                {`O usuário não possui a permissão de ${roleTabLabel} necessária para esta aba.`}
                            </Typography>
                        )}

                        {hasRequiredRole && alreadyLinked && (
                            <Typography className={classes.warning}>
                                O usuário já está vinculado a este cliente.
                            </Typography>
                        )}
                    </div>
                )}

            </DialogContent>

            <DialogActions className={classes.actionsFlex}>

                <UnoButton onClick={() => handleClose(false)} color="secondary">
                    Cancelar
                </UnoButton>

                <UnoButton
                    onClick={doLink}
                    disabled={!canLink}
                    type="primary">
                    Vincular
                </UnoButton>

            </DialogActions>

        </Dialog>
    );
}
